import { PageToolbar } from '@app/components/page-toolbar/PageToolbar';
import { ChipNavigationMui } from '@mi-front/ui';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom';
import { useAdoverlayEnabledForActiveStation } from '../hooks/useAdoverlayEnabled';
import { useLiveMentionsEnabledForActiveStation } from '../hooks/useLiveMentionsEnabledForActiveStation';
import { MatchCategory } from '../screens/matches-list/types';

type MatchCategoryMeta = {
  id: string;
  path: string;
  label: string;
};

export const AuditMatchCategoryNavigator = ({
  children,
  additionalCategories = [],
}: {
  children: (category: string) => React.ReactNode;
  additionalCategories?: MatchCategoryMeta[];
}) => {
  const match = useRouteMatch();
  const location = useLocation();
  const liveMentionsEnabled = useLiveMentionsEnabledForActiveStation();
  const adOverlayEnabled = useAdoverlayEnabledForActiveStation();

  const pathBase = match.url.replace(/\/$/, '') || match.url;
  const isAtBasePath = location.pathname === pathBase || location.pathname === pathBase + '/';

  const chips = React.useMemo(() => {
    const items = [
      {
        value: 'spot',
        label: <FormattedMessage id="audit.matchCategory.customAds" defaultMessage="CUSTOM ADS" />,
        content: children(MatchCategory.ADS),
      },
      ...(liveMentionsEnabled
        ? [
            {
              value: 'pnt',
              label: (
                <FormattedMessage id="audit.matchCategory.mentions" defaultMessage="MENTIONS" />
              ),
              content: children(MatchCategory.LIVE_MENTIONS),
            },
          ]
        : []),
      ...(adOverlayEnabled
        ? [
            {
              value: 'adoverlay',
              label: (
                <FormattedMessage
                  id="audit.matchCategory.adOverlays"
                  defaultMessage="AD OVERLAYS"
                />
              ),
              content: children(MatchCategory.AD_OVERLAY),
            },
          ]
        : []),
      ...(liveMentionsEnabled
        ? additionalCategories.map(({ id, path, label }) => ({
            value: path,
            label,
            content: children(id),
          }))
        : []),
    ];
    return items;
  }, [children, liveMentionsEnabled, adOverlayEnabled, additionalCategories]);

  if (isAtBasePath) {
    return <Redirect to={`${pathBase}/spot`} />;
  }

  return (
    <ChipNavigationMui
      pathBase={pathBase}
      defaultActiveChip="spot"
      chips={chips}
      Wrapper={PageToolbar}
    />
  );
};
